import { StackedPhotos } from '@/components/StackedPhotos';
import { UserAvatar } from '@/components/UserAvatar';
import { Divider } from '@/trailmix/Divider';
import { ShareButton } from '@/components/ShareButton';
import { ProseDisplay } from '@/components/Tiptap';
import { PlaceList, PlaceListBookmark } from '@/pages/types';
import { IconButton } from '@/trailmix/IconButton';
import { Tooltip } from '@chakra-ui/react';
import { Link } from '@inertiajs/react';
import { mdiCog, mdiImageMultiple, mdiMapMarkerPlus, mdiTimeline, mdiViewList } from '@mdi/js';
import Icon from '@mdi/react';
import { IconProps } from '@mdi/react/dist/IconProps';
import { useFilterQueryParam } from '@/filters/useFilterQueryParam';
import { PlaceNameFilterControl } from '@/pages/places/components/PlaceNameFilterControl';
import { PlaceStatusFilterControl } from '@/pages/places/components/PlaceStatusFilterControl';
import { BookmarkListButton } from './BookmarkListButton';
import { GuestBookmarkCTA } from './GuestBookmarkCTA';
import { Button } from '@/trailmix/Button';
import { cn } from '@/trailmix/lib/cn';
import { getVisibilityLabel } from '@/pages/lists/utils/placeListVisibility';
import { useAuth } from '@/auth/useAuth';

function PlaceListNavigationButton({
    href,
    active,
    iconPath,
    children,
}: {
    href: string;
    active: boolean;
    iconPath: IconProps['path'];
    children: React.ReactNode;
}) {
    return (
        <div
            className={cn(
                '-mb-0.5 rounded-t-md transition',
                active ? 'border-b-2 border-red-300' : 'hover:border-b-2 hover:border-gray-400'
            )}
        >
            <Link
                href={href}
                preserveScroll
                className={cn(
                    'flex h-10 items-center gap-2 px-3.5 font-medium text-gray-700',
                    active ? 'font-semibold' : ''
                )}
            >
                <Icon path={iconPath} className='size-5' />
                {children}
            </Link>
        </div>
    );
}

type PlaceListLayoutProps = {
    list: PlaceList;
    placeListBookmark: PlaceListBookmark | null;
    canUpdateList: boolean;
    canBookmarkList: boolean;
    hideFilters?: boolean;
    children: React.ReactNode;
};

export function PlaceListLayout({
    list,
    placeListBookmark,
    canUpdateList,
    canBookmarkList,
    hideFilters = false,
    children,
}: PlaceListLayoutProps) {
    const auth = useAuth();
    const {
        value: placeStatusFilterValue,
        isModified: isPlaceStatusFilterModified,
        onReset: onPlaceStatusFilterReset,
        onChange: onPlaceStatusFilterChange,
    } = useFilterQueryParam({ filterId: 'status', defaultValue: 'all' });

    return (
        <>
            <header className='space-y-4'>
                <div className='flex items-start justify-between'>
                    <div className='flex flex-col gap-1.5'>
                        <h1 className='text-2xl font-bold'>{list.name}</h1>

                        <div className='flex items-center'>
                            <span className='text-sm font-medium text-gray-600'>List by</span>
                            <Link href={route('profile.show', [list.user])} className='ml-1.5 flex items-center'>
                                <UserAvatar user={list.user} size='2xs' />
                                <span className='ml-1.5 text-sm font-medium text-gray-800'>{list.user.name}</span>
                            </Link>
                            <span className='mx-2 text-gray-300'>|</span>
                            <span className='text-sm text-gray-500'>{getVisibilityLabel(list.visibility)}</span>
                        </div>
                    </div>

                    {canUpdateList && (
                        <Tooltip label='List Settings'>
                            <IconButton
                                icon={<Icon path={mdiCog} className='size-5' />}
                                as={Link}
                                href={route('lists.edit', [list.user, list])}
                                size='sm'
                                variant='ghost'
                                color='gray.600'
                                aria-label='List Settings'
                            />
                        </Tooltip>
                    )}
                </div>

                {list.description && <ProseDisplay content={list.description} className='my-0' />}

                {(list.recent_visit_photos ?? []).length > 0 && (
                    <Link
                        href={route('lists.photos', [list.user, list])}
                        className='block transition-opacity hover:opacity-75'
                    >
                        <StackedPhotos
                            photos={list.recent_visit_photos ?? []}
                            totalCount={list.total_visit_photo_count}
                        />
                    </Link>
                )}

                {list.show_progress_bar && (list.total_place_count ?? 0) > 0 && (
                    <div>
                        <div className='mb-1.5 text-sm'>
                            <span className='font-medium text-gray-800'>{list.total_visited_place_count ?? 0}</span>
                            <span className='mx-1 text-gray-400'>/</span>
                            <span className='text-gray-500'>{list.total_place_count} places visited</span>
                        </div>
                        <div className='h-1.5 w-full overflow-hidden rounded-full bg-gray-200'>
                            <div
                                className='h-full rounded-full bg-green-500'
                                style={{
                                    width: `${Math.min(100, Math.max(0, ((list.total_visited_place_count ?? 0) / list.total_place_count!) * 100))}%`,
                                }}
                            />
                        </div>
                    </div>
                )}

                <Divider />

                <div className='grid grid-cols-2 gap-2'>
                    <ShareButton size='md' />
                    <BookmarkListButton
                        list={list}
                        placeListBookmark={placeListBookmark}
                        canBookmarkList={canBookmarkList}
                    />
                    {canUpdateList && (
                        <Button
                            as={Link}
                            href={route('search.index', { fromList: list.slug })}
                            leftIcon={<Icon path={mdiMapMarkerPlus} className='size-4' />}
                        >
                            Add place
                        </Button>
                    )}
                </div>
            </header>

            <div className='mt-4 flex flex-wrap items-center justify-between gap-2 border-b-2 border-gray-200'>
                <div className='flex flex-wrap'>
                    <PlaceListNavigationButton
                        href={route('lists.show', [list.user, list])}
                        active={route().current('lists.show')}
                        iconPath={mdiViewList}
                    >
                        List
                    </PlaceListNavigationButton>
                    {list.show_timeline_section && (
                        <PlaceListNavigationButton
                            href={route('lists.timeline', [list.user, list])}
                            active={route().current('lists.timeline')}
                            iconPath={mdiTimeline}
                        >
                            Timeline
                        </PlaceListNavigationButton>
                    )}
                    <PlaceListNavigationButton
                        href={route('lists.photos', [list.user, list])}
                        active={route().current('lists.photos')}
                        iconPath={mdiImageMultiple}
                    >
                        Photos
                    </PlaceListNavigationButton>
                </div>
            </div>

            {!hideFilters && (list.total_place_count ?? 0) !== 0 && (
                <div className='flex flex-wrap items-center gap-2 border-b-2 border-gray-200 py-2'>
                    <PlaceNameFilterControl className='flex-1' />
                    <PlaceStatusFilterControl
                        value={placeStatusFilterValue}
                        isModified={isPlaceStatusFilterModified}
                        onChange={onPlaceStatusFilterChange}
                        onReset={onPlaceStatusFilterReset}
                    />
                </div>
            )}

            {children}

            {!auth.user && <GuestBookmarkCTA list={list} />}
        </>
    );
}
